import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Heart, BookOpen, Users, Globe } from "lucide-react";

const COMMUNITY_IMG = "https://customer-assets.emergentagent.com/job_faith-bubbles-io/artifacts/rc4l8l1b_image.png";

const values = [
  { icon: Heart, title: "Love", desc: "We are a community bound by the love of Christ", color: "bg-red-50 text-red-600" },
  { icon: BookOpen, title: "Faith", desc: "Grounded in the Word of God for victorious living", color: "bg-amber-50 text-amber-600" },
  { icon: Users, title: "Community", desc: "Building lasting relationships and growing together", color: "bg-red-50 text-red-600" },
  { icon: Globe, title: "Mission", desc: "Reaching the world with the gospel of Jesus Christ", color: "bg-amber-50 text-amber-600" },
];

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-white relative overflow-hidden">
      {/* Decorative bubbles */}
      <div className="bubble-decoration bg-red-400 w-48 h-48 top-20 -right-24 animate-pulse-soft" />
      <div className="bubble-decoration bg-amber-300 w-32 h-32 bottom-20 -left-16 animate-pulse-soft" style={{ animationDelay: "1.5s" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="rounded-[2.5rem] overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.08)]">
              <img
                src={COMMUNITY_IMG}
                alt="Church community gathering"
                className="w-full h-[450px] object-cover"
              />
            </div>
            {/* Floating accent card */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-5 -right-5 bg-red-600 text-white rounded-2xl p-5 shadow-xl"
            >
              <p className="text-3xl font-bold">44+</p>
              <p className="text-sm opacity-90">Years of Faith</p>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="space-y-6"
          >
            <span className="font-accent text-2xl text-amber-600">About Us</span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-stone-900">
              A Place to <span className="text-red-600">Belong</span>
            </h2>
            <p className="text-base md:text-lg text-stone-600 leading-relaxed">
              Winners Chapel International Ipswich is a vibrant, faith-filled community
              dedicated to the preaching of the Word of Faith. We believe in the power of
              God's Word to transform lives and build champions. Whether you're new to
              faith or a seasoned believer, you'll find a warm and welcoming family here.
            </p>
            <p className="text-base text-stone-500 leading-relaxed">
              Located at Gainsborough Sports Centre, we gather every Sunday at 10:00 AM
              for our Sunday Service and Wednesday at 6:30 PM for our Wednesday
              Service. Come as you are and experience the love and power of God in a
              life-changing way.
            </p>

            {/* Values grid */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              {values.map((v, i) => (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                  className="bubbly-card bg-white p-5 rounded-2xl border border-stone-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)]"
                  data-testid={`value-card-${v.title.toLowerCase()}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${v.color} mb-3`}>
                    <v.icon size={20} />
                  </div>
                  <h3 className="font-semibold text-stone-900 text-sm">{v.title}</h3>
                  <p className="text-xs text-stone-500 mt-1">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
